import React from 'react';
import { ReviewTrace } from '../../../api/structs';

const outcomeClass = (outcome: string): string => {
  if (outcome === 'pass') return 'text-emerald-300';
  if (outcome === 'fail') return 'text-amber-200';
  if (outcome === 'not_called') return 'text-zinc-500';
  // unparseable / truncated / error — the reviewer could not turn it into a verdict.
  return 'text-red-300';
};

/** "none" is the backend's word for a stream that ended without saying why. */
const finishLabel = (reason: string): string => (reason === 'none' ? 'no finish reason' : reason);

/** UTF-8 byte length — the unit the backend caps its trace heads in. */
const utf8Length = (text: string): number => encodeURIComponent(text).replace(/%[0-9A-F]{2}/gi, 'x').length;
const HEAD_BYTES = 2048;

/**
 * A trace served off the live run carries 2 KB heads of the prompt and the
 * answer. It is SHORTENED when either head is cut — the prompt was longer
 * than its head, or the answer filled the head to the cap.
 */
export const isShortenedTrace = (trace: ReviewTrace, source?: string): boolean => {
  if (source !== 'run') return false;
  const prompt = trace.system_prompt || trace.prompt_head || '';
  const answer = trace.raw_output || trace.output_head || '';
  return trace.prompt_chars > utf8Length(prompt) || utf8Length(answer) >= HEAD_BYTES - 4;
};

interface ReviewTraceListProps {
  /** Already filtered to the verdict this list sits under. */
  traces: ReviewTrace[];
  loading?: boolean;
  /** Why the trace fetch failed, if it did. */
  error?: string;
  /** Rendered on the heading line — the panel's "Show the reviewer" control. */
  action?: React.ReactNode;
  /** Where the traces were read: "store" = full text, "run" = the capped heads. */
  source?: 'store' | 'run' | '';
}

/**
 * What the reviewer actually saw and said, one row per raw call: which tier
 * and model ran, how many images went in, how long it took, what came back,
 * and why the reply stopped when it did not finish. The prompt hides behind
 * "What it read" and the reply behind "What it answered" — the full text when
 * the trace came from the store, the 2 KB heads when it was read off a live
 * run before the store write landed, and then a row whose head was cut says
 * it is a shortened copy.
 */
const ReviewTraceList: React.FC<ReviewTraceListProps> = ({ traces, loading, error, action, source }) => (
  <div className="space-y-1 border-t border-white/10 pt-2" data-testid="review-trace-list">
    <div className="flex items-center gap-2">
      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Inside the reviewer</p>
      {action}
    </div>

    {error && <p className="text-[10px] text-red-300">{error}</p>}

    {loading && traces.length === 0 && !error && (
      <p className="text-[10px] text-zinc-600">Reading the reviewer log…</p>
    )}

    {traces.length === 0 && !loading && !error && (
      <p data-testid="review-trace-empty" className="text-[10px] text-zinc-600">
        No trace recorded for this verdict (runs before 2026-09 have none).
      </p>
    )}

    <ul className="space-y-1">
      {traces.map((trace) => (
        <li key={trace.id} data-testid="review-trace-row" className="text-[10px] text-zinc-400">
          <span className="text-zinc-600">{new Date(trace.created_at).toLocaleTimeString()}</span>{' '}
          <span className="uppercase text-zinc-300">{trace.tier}</span>{' '}
          <span className="font-mono">{trace.model}</span> · {trace.images?.length ?? 0} images ·{' '}
          {trace.prompt_chars} chars · {(trace.latency_ms / 1000).toFixed(1)} s ·{' '}
          <span className={outcomeClass(trace.outcome)}>{trace.outcome}</span>
          {trace.finish_reason && (
            <span data-testid="review-trace-finish" className="text-red-300"> · stopped: {finishLabel(trace.finish_reason)}</span>
          )}
          {isShortenedTrace(trace, source) && (
            <span data-testid="review-trace-shortened" className="text-amber-300"> · shortened — full record unavailable</span>
          )}
          <details className="mt-0.5">
            <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-zinc-500">
              What it read
            </summary>
            <p className="text-[10px] text-zinc-500">
              {(trace.images ?? []).map((image) => image.label).join(' · ')}
            </p>
            <pre
              data-testid="review-trace-prompt"
              className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap border border-white/10 bg-black/70 p-2 font-mono text-[11px] text-slate-300"
            >
              {trace.system_prompt || trace.prompt_head}
            </pre>
          </details>
          {/* A judge that was never asked gave no answer: the text a
              not-called trace carries is the engine's reason for not asking. */}
          <details className="mt-0.5">
            <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-zinc-500">
              {trace.outcome === 'not_called' ? 'Why it was not asked' : 'What it answered'}
            </summary>
            <pre
              data-testid="review-trace-answer"
              className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap border border-white/10 bg-black/70 p-2 font-mono text-[11px] text-slate-300"
            >
              {trace.raw_output ||
                trace.output_head ||
                (trace.outcome === 'not_called' ? '(no reason recorded)' : '(no answer)')}
            </pre>
          </details>
        </li>
      ))}
    </ul>
  </div>
);

export default ReviewTraceList;
